import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DataTable, Button, Modal } from '../../Components/UI';
import {
  MdCallMissedOutgoing,
  MdNature,
  MdAccessibility,
} from 'react-icons/md';
import {
  FaQuestionCircle,
  FaQuoteLeft,
  FaCommentAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaExternalLinkAlt,
  FaInbox,
} from 'react-icons/fa';
import { GrContact } from 'react-icons/gr';
import { GiProgression } from 'react-icons/gi';
import { FiCheck, FiEye, FiRefreshCw, FiRotateCcw } from 'react-icons/fi';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import fetchAllPages, { formatExportDateTime } from '../../UtilityHookFunctions/fetchAllPages';

const TYPE_CONFIG = {
  enquiries: {
    label: 'Enquiries',
    icon: <GrContact className="w-3.5 h-3.5 text-purple-600" />,
    badgeClass: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  callBack: {
    label: 'Call Back',
    icon: <MdCallMissedOutgoing className="w-3.5 h-3.5 text-amber-600" />,
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  questions: {
    label: 'Questions',
    icon: <FaQuestionCircle className="w-3.5 h-3.5 text-sky-600" />,
    badgeClass: 'bg-sky-50 text-sky-700 border-sky-200',
  },
  testimonials: {
    label: 'Testimonials',
    icon: <FaQuoteLeft className="w-3.5 h-3.5 text-violet-600" />,
    badgeClass: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  sharedThoughts: {
    label: 'Shared Thoughts',
    icon: <FaCommentAlt className="w-3.5 h-3.5 text-blue-600" />,
    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  healthPackages: {
    label: 'Health Packages',
    icon: <MdAccessibility className="w-3.5 h-3.5 text-emerald-600" />,
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  calmNatural: {
    label: 'Calm Ailment',
    icon: <MdNature className="w-3.5 h-3.5 text-teal-600" />,
    badgeClass: 'bg-teal-50 text-teal-700 border-teal-200',
  },
  bookTherapies: {
    label: 'Book Therapies',
    icon: <GiProgression className="w-3.5 h-3.5 text-indigo-600" />,
    badgeClass: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  contactUs: {
    label: 'Contact Us',
    icon: <FaEnvelope className="w-3.5 h-3.5 text-slate-600" />,
    badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
  },
};

export default function OpenInteractionsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [countsByType, setCountsByType] = useState({});
  const [activeType, setActiveType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open'); // 'open' | 'all' | 'closed'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchInteractions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${END_POINT}/interactions/all`, {
        headers: {
          Authorization: getJwtToken(),
        },
        params: {
          status: statusFilter,
          page,
          limit,
          search: searchTerm,
          type: activeType,
        },
      });

      if (res.data?.success) {
        setData(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
        setCountsByType(res.data.countsByType || {});
      }
    } catch (err) {
      console.error('Failed to fetch interactions:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, activeType, statusFilter]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  // Exports every interaction matching the current status/type/search filters.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, pageSize) => {
        const res = await axios.get(`${END_POINT}/interactions/all`, {
          headers: { Authorization: getJwtToken() },
          params: { status: statusFilter, page: pageNo, limit: pageSize, search: searchTerm, type: activeType },
        });
        return res.data?.data || [];
      });
      if (!all.length) {
        toast.info('No interactions to export');
        return;
      }
      exportToExcel(all, `Interactions_${statusFilter}`, [
        {
          key: 'typeKey',
          label: 'Interaction Type',
          format: (val, row) => TYPE_CONFIG[val]?.label || row.interactionType || '',
        },
        { key: 'name', label: 'Name' },
        { key: 'mobileNo', label: 'Mobile' },
        { key: 'email', label: 'Email' },
        { key: 'subject', label: 'Subject' },
        { key: 'details', label: 'Message' },
        { key: 'createdAt', label: 'Received', format: formatExportDateTime },
        { key: 'status', label: 'Status' },
      ], 'Interactions');
    } catch (err) {
      console.error('Failed to export interactions:', err);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpdateStatus = async (row, nextStatus) => {
    const targetId = row._id || row.id;
    setClosingId(targetId);
    try {
      const res = await axios.post(
        `${END_POINT}/interactions/update-status`,
        {
          typeKey: row.typeKey,
          id: targetId,
          status: nextStatus,
        },
        {
          headers: {
            Authorization: getJwtToken(),
          },
        }
      );

      if (res.data?.success) {
        toast.success(
          nextStatus === 'closed'
            ? `Ticket for "${row.name}" marked as closed!`
            : `Ticket for "${row.name}" reopened!`
        );
        if (selectedRow && (selectedRow._id === targetId || selectedRow.id === targetId)) {
          setSelectedRow(null);
        }
        fetchInteractions();
      }
    } catch (err) {
      toast.error('Failed to update ticket status');
    } finally {
      setClosingId(null);
    }
  };

  const tabs = [
    { key: 'all', label: 'All Categories', count: countsByType.all ?? 0 },
    { key: 'enquiries', label: 'Enquiries', count: countsByType.enquiries ?? 0 },
    { key: 'callBack', label: 'Call Back', count: countsByType.callBack ?? 0 },
    { key: 'questions', label: 'Questions', count: countsByType.questions ?? 0 },
    { key: 'testimonials', label: 'Testimonials', count: countsByType.testimonials ?? 0 },
    { key: 'sharedThoughts', label: 'Shared Thoughts', count: countsByType.sharedThoughts ?? 0 },
    { key: 'healthPackages', label: 'Health Packages', count: countsByType.healthPackages ?? 0 },
    { key: 'calmNatural', label: 'Calm Ailment', count: countsByType.calmNatural ?? 0 },
    { key: 'bookTherapies', label: 'Book Therapies', count: countsByType.bookTherapies ?? 0 },
    { key: 'contactUs', label: 'Contact Us', count: countsByType.contactUs ?? 0 },
  ];

  const tableHeaders = [
    {
      label: 'Interaction Type',
      key: 'typeKey',
      render: (val, row) => {
        const conf = TYPE_CONFIG[row.typeKey] || {
          label: row.interactionType || 'Interaction',
          icon: <FaInbox className="w-3.5 h-3.5 text-slate-500" />,
          badgeClass: 'bg-slate-50 text-slate-700 border-slate-200',
        };
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${conf.badgeClass}`}
          >
            {conf.icon}
            <span>{conf.label}</span>
          </span>
        );
      },
    },
    {
      label: 'Sender / Name',
      key: 'name',
      render: (val, row) => (
        <div>
          <div className="font-semibold text-slate-900">{row.name || 'Visitor'}</div>
          {row.mobileNo && (
            <a
              href={`tel:${row.mobileNo}`}
              className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 mt-0.5"
            >
              <FaPhoneAlt className="w-2.5 h-2.5" />
              {row.mobileNo}
            </a>
          )}
        </div>
      ),
    },
    {
      label: 'Topic / Subject',
      key: 'subject',
      render: (val, row) => (
        <div className="max-w-[180px]">
          <span className="font-medium text-slate-800 text-xs block truncate" title={row.subject}>
            {row.subject || '—'}
          </span>
          {row.email && (
            <a
              href={`mailto:${row.email}`}
              className="text-[11px] text-slate-500 hover:text-slate-700 truncate block mt-0.5"
              title={row.email}
            >
              {row.email}
            </a>
          )}
        </div>
      ),
    },
    {
      label: 'Inquiry / Message',
      key: 'details',
      render: (val, row) => (
        <div className="max-w-[220px]">
          <p
            className="text-xs text-slate-600 line-clamp-2 leading-relaxed"
            title={row.details}
          >
            {row.details || '—'}
          </p>
        </div>
      ),
    },
    {
      label: 'Received',
      key: 'createdAt',
      render: (val) => {
        if (!val) return '—';
        try {
          const d = new Date(val);
          return (
            <span className="text-xs text-slate-500 whitespace-nowrap">
              {d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })},{' '}
              {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          );
        } catch {
          return '—';
        }
      },
    },
    {
      label: 'Status',
      key: 'status',
      render: (val, row) => (
        row.status === 'closed' ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            <span className="w-2 h-2 rounded-full bg-slate-400"></span>
            <span>Closed</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>Pending</span>
          </span>
        )
      ),
    },
    {
      label: 'Actions',
      key: '_id',
      render: (id, row) => {
        const isBusy = closingId === (row._id || row.id);
        const isClosed = row.status === 'closed';
        return (
          <div className="flex items-center gap-1.5">
            {/* Toggle Status Button */}
            {isClosed ? (
              <Button
                size="xs"
                variant="outline"
                disabled={isBusy}
                onClick={() => handleUpdateStatus(row, 'open')}
                title="Reopen Ticket"
                className="text-slate-600 border-slate-300 hover:bg-slate-50 font-semibold shadow-xs"
              >
                <FiRotateCcw className="w-3.5 h-3.5 text-slate-500" />
                <span>{isBusy ? 'Updating…' : 'Reopen'}</span>
              </Button>
            ) : (
              <Button
                size="xs"
                variant="outline"
                disabled={isBusy}
                onClick={() => handleUpdateStatus(row, 'closed')}
                title="Mark Ticket as Closed"
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 hover:border-emerald-400 font-semibold shadow-xs"
              >
                <FiCheck className="w-3.5 h-3.5 text-emerald-600" />
                <span>{isBusy ? 'Closing…' : 'Close'}</span>
              </Button>
            )}

            {/* View Details */}
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setSelectedRow(row)}
              title="View Submission Details"
              className="text-sky-600 hover:bg-sky-50"
            >
              <FiEye className="w-3.5 h-3.5" />
            </Button>

            {/* Direct Link to section */}
            {row.route && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => navigate(row.route)}
                title={`Open full ${row.interactionType} page`}
                className="text-slate-500 hover:text-slate-800"
              >
                <FaExternalLinkAlt className="w-3 h-3" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      {/* Header with Title & Live Open Count & Status Filter */}
      <div className="p-4 md:p-5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-gradient-to-r from-slate-50/60 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-500/10 text-primary-600 flex items-center justify-center font-bold text-lg border border-primary-500/20">
            <FaInbox className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-base md:text-lg text-slate-800 tracking-tight">
                Pending Inbound Interactions & Leads
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                {countsByType.all ?? totalCount} {statusFilter === 'closed' ? 'Closed' : statusFilter === 'all' ? 'Total' : 'Open'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Live website queries, callbacks, package sign-ups & inquiries awaiting ticket closure
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start lg:self-auto flex-wrap">
          {/* Status Filter Toggle */}
          <div className="inline-flex rounded-xl bg-slate-100 p-1 border border-slate-200 text-xs">
            <button
              onClick={() => { setStatusFilter('open'); setPage(1); }}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'open'
                  ? 'bg-white text-slate-900 shadow-xs font-semibold'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Pending / Open
            </button>
            <button
              onClick={() => { setStatusFilter('all'); setPage(1); }}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'all'
                  ? 'bg-white text-slate-900 shadow-xs font-semibold'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All Leads
            </button>
            <button
              onClick={() => { setStatusFilter('closed'); setPage(1); }}
              className={`px-3 py-1 rounded-lg font-medium transition ${
                statusFilter === 'closed'
                  ? 'bg-white text-slate-900 shadow-xs font-semibold'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Closed
            </button>
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={fetchInteractions}
            disabled={loading}
            title="Refresh inquiries"
            className="text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            <FiRefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            isLoading={isExporting}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900"
          >
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Type Filter Tabs */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto bg-slate-50/40">
        {tabs.map((tab) => {
          const isActive = activeType === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                setActiveType(tab.key);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition whitespace-nowrap flex items-center gap-1.5 ${
                isActive
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  isActive
                    ? 'bg-slate-700 text-white'
                    : tab.count > 0
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-200 text-slate-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Unified Open Interactions Table */}
      <div className="p-1">
        <DataTable
          headers={tableHeaders}
          data={data}
          page={page}
          totalPages={totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          onSearch={(term) => {
            setSearchTerm(term);
            setPage(1);
          }}
          isLoading={loading}
          emptyMessage="🎉 All caught up! No interaction tickets or unresolved inquiries matching your filter."
        />
      </div>

      {/* Details Modal */}
      <Modal
        isOpen={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        title={`${selectedRow?.interactionType || 'Interaction'} Details`}
        subtitle="Full submitted payload"
        maxWidth="max-w-xl"
        footer={
          <div className="flex items-center justify-between w-full">
            {selectedRow && (
              selectedRow.status === 'closed' ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleUpdateStatus(selectedRow, 'open')}
                  disabled={closingId === (selectedRow._id || selectedRow.id)}
                  className="font-semibold text-slate-700"
                >
                  <FiRotateCcw className="w-4 h-4 mr-1.5 text-slate-600" />
                  Reopen Ticket
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleUpdateStatus(selectedRow, 'closed')}
                  disabled={closingId === (selectedRow._id || selectedRow.id)}
                  className="bg-emerald-600 hover:bg-emerald-700 border-emerald-600 text-white font-semibold"
                >
                  <FiCheck className="w-4 h-4 mr-1.5" />
                  Mark as Closed
                </Button>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedRow(null)}
            >
              Close Window
            </Button>
          </div>
        }
      >
        {selectedRow && (
          <div className="space-y-3 text-xs">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-500 uppercase tracking-wider text-[11px]">
                  Source:
                </span>
                <span className="font-bold text-slate-800 text-sm">
                  {selectedRow.interactionType}
                </span>
              </div>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                Open Ticket
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(selectedRow.raw || selectedRow)
                .filter(([k]) => !['_id', '__v', 'status'].includes(k))
                .map(([key, val]) => (
                  <div key={key} className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] block">
                      {key.replace(/([A-Z])/g, ' $1')}
                    </span>
                    <span className="text-slate-800 text-sm mt-0.5 block break-words">
                      {typeof val === 'object' ? JSON.stringify(val) : String(val || '—')}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
